import { DataGlueGraph, NodeInstance } from '@glueapp/graph-types';
import { ValidationError, ValidationRuleResult } from '../ValidationRule';
import { Node } from '@glueapp/graph-types';
import { IntegrationAccess } from '@glueapp/graph-types';

export const IntegrationAvailabilityRule = (
    graph: DataGlueGraph,
    nodeLibraryDictionary?: { [key: string]: Node },
    availableIntegrationAccesses?: IntegrationAccess[],
): ValidationRuleResult => {
    const errors: ValidationError[] = [];

    // Check main graph nodes
    const mainGraphErrors = validateNodesForIntegrationAccess(
        graph.nodeInstances,
        nodeLibraryDictionary || {},
        availableIntegrationAccesses || [],
        'main',
    );
    errors.push(...mainGraphErrors);

    // Check function nodes
    if (graph.functions) {
        for (const func of graph.functions) {
            const functionErrors = validateNodesForIntegrationAccess(
                func.nodeInstances,
                nodeLibraryDictionary || {},
                availableIntegrationAccesses || [],
                func.id,
            );
            errors.push(...functionErrors);
        }
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

function validateNodesForIntegrationAccess(
    nodeInstances: NodeInstance[],
    nodeLibraryDictionary: { [key: string]: Node },
    availableIntegrationAccesses: IntegrationAccess[],
    context: string,
): ValidationError[] {
    const errors: ValidationError[] = [];

    // Group available accesses by provider for faster lookup
    const accessesByProvider = availableIntegrationAccesses.reduce<{ [provider: string]: IntegrationAccess[] }>((acc, access) => {
        if (!acc[access.provider]) {
            acc[access.provider] = [];
        }
        acc[access.provider].push(access);
        return acc;
    }, {});

    for (const nodeInstance of nodeInstances) {
        const nodeDefinition = nodeLibraryDictionary[nodeInstance.nodeId];

        if (!nodeDefinition?.integration) {
            continue; // Skip nodes that don't require integrations
        }

        const requiredProvider = nodeDefinition.integration.provider;
        const availableAccessesForProvider = accessesByProvider[requiredProvider];

        // Check if any integration access is available for this provider
        if (!availableAccessesForProvider || availableAccessesForProvider.length === 0) {
            errors.push({
                code: 'MISSING_INTEGRATION_ACCESS',
                message: `Integration access not available for provider '${requiredProvider}'. Connect to ${requiredProvider} in your integrations settings.`,
                severity: 'error',
                location: {
                    nodeId: nodeInstance.id,
                    functionId: context !== 'main' ? context : undefined,
                    provider: requiredProvider,
                },
            });
        }
        // Check if the node has selected an integration access
        else if (!nodeInstance.selectedIntegrationAccess) {
            errors.push({
                code: 'MISSING_SELECTED_INTEGRATION',
                message: `Node requires ${requiredProvider} integration but none is selected. Select an integration account for this node.`,
                severity: 'error',
                location: {
                    nodeId: nodeInstance.id,
                    functionId: context !== 'main' ? context : undefined,
                    provider: requiredProvider,
                },
            });
        }
        // Check if the selected integration access is still valid/available
        else {
            const selectedAccessId = nodeInstance.selectedIntegrationAccess.id;
            const isSelectedAccessAvailable = availableAccessesForProvider.some(access => access.id === selectedAccessId);

            if (!isSelectedAccessAvailable) {
                errors.push({
                    code: 'INVALID_SELECTED_INTEGRATION',
                    message: `Node's selected ${requiredProvider} integration is no longer available. Please select a different integration or reconnect to ${requiredProvider}.`,
                    severity: 'error',
                    location: {
                        nodeId: nodeInstance.id,
                        functionId: context !== 'main' ? context : undefined,
                        provider: requiredProvider,
                        selectedIntegrationId: selectedAccessId,
                    },
                });
            }
        }
    }

    return errors;
}
